import { Button, IconButton } from '@ads/components-react';
import { DashboardFilters as DashboardFiltersType } from '@linka/domain/dtos';
import { ticketQueries } from '@linka/http/ticket';
import { useTranslation } from '@linka/i18n';
import { endOfTodayUtc } from '@linka/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { FunnelIcon } from 'lucide-react';
import { useReducer } from 'react';
import { useParams } from 'react-router';
import { DateRangePicker } from '../../date-range-picker/date-range-picker';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../dialog/dialog';
import { MultiSelect } from '../../multi-select/multi-select';

const dashboardDraftFiltersAtom = atom<DashboardFiltersType>({
  startDate: new Date(new Date().getFullYear(), 0, 1).toISOString(),
  endDate: endOfTodayUtc().toISOString(),
  paymentMethods: [],
  ticketsIds: [],
});
export const dashboardFiltersAtom = atom<DashboardFiltersType>({
  startDate: new Date(new Date().getFullYear(), 0, 1).toISOString(),
  endDate: endOfTodayUtc().toISOString(),
  paymentMethods: [],
  ticketsIds: [],
});

export function DashboardFilters({ children }: React.PropsWithChildren) {
  const t = useTranslation();
  const [isModalOpen, toggleModal] = useReducer((prev) => !prev, false);
  const dashboardDraftFilters = useAtomValue(dashboardDraftFiltersAtom);
  const [dashboardFilters, setDashboardFilters] = useAtom(dashboardFiltersAtom);

  const dashboardFiltersCount = [
    Boolean(dashboardFilters.startDate && dashboardFilters.endDate),
    dashboardFilters.paymentMethods && dashboardFilters.paymentMethods?.length > 0,
    dashboardFilters.ticketsIds && dashboardFilters.ticketsIds?.length > 0,
  ].filter(Boolean).length;

  const handleApplyFilters = () => {
    setDashboardFilters(dashboardDraftFilters);
    toggleModal();
  };
  const handleClearFilters = () => {
    setDashboardFilters({
      startDate: undefined,
      endDate: undefined,
      paymentMethods: [],
      ticketsIds: [],
    });
    toggleModal();
  };

  return (
    <>
      <Dialog open={isModalOpen} onOpenChange={toggleModal}>
        <div id='dashboard-filters-mobile' className='flex size-fit relative sm:hidden'>
          <DialogTrigger asChild>
            <IconButton
              icon={FunnelIcon}
              hierarchy='secondary'
              size='sm'
              ariaLabel={t('export-flow.filters')}
              className='relative'
            />
          </DialogTrigger>
          {Boolean(dashboardFiltersCount) && (
            <div className='absolute -top-quark -right-quark rounded-pill size-mini flex items-center justify-center bg-ctx-content-button'>
              <span className='!leading-default text-base-neutral-white ts-paragraph-xxxs'>
                {dashboardFiltersCount}
              </span>
            </div>
          )}
        </div>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('export-flow.filters')}</DialogTitle>
          </DialogHeader>
          <div className='my-mini flex flex-col gap-micro'>{children}</div>
          <DialogFooter className='flex flex-col gap-mini w-full'>
            <Button fullWidth onClick={handleApplyFilters}>
              {t('apply-filters')}
            </Button>
            <Button fullWidth hierarchy='secondary' onClick={handleClearFilters}>
              {t('clear-filters')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div
        id='dashboard-filters-desktop'
        className='hidden sm:flex flex-col lg:items-start lg:flex-row flex-wrap gap-micro col-span-2 w-full sm:!w-auto'
      >
        {children}
      </div>
    </>
  );
}

export const DashboardFiltersTickets = () => {
  const t = useTranslation();
  const { eventId = '' } = useParams();
  const { data: tickets } = useSuspenseQuery(ticketQueries.list(eventId));
  const [dashboardFilters, setDashboardFilters] = useAtom(dashboardFiltersAtom);
  const setDashboardDraftFilters = useSetAtom(dashboardDraftFiltersAtom);
  const isDesktop = window.matchMedia('(min-width: 640px)').matches;
  return (
    <MultiSelect
      optionsLimit={3}
      label={t('ticket')}
      placeholder={t('select-ticket-type')}
      defaultValue={dashboardFilters.ticketsIds}
      onValueChange={(paymentMethods) => {
        if (isDesktop) {
          setDashboardFilters((prev) => ({
            ...prev,
            ticketsIds: paymentMethods as DashboardFiltersType['ticketsIds'],
          }));
        } else {
          setDashboardDraftFilters((prev) => ({
            ...prev,
            ticketsIds: paymentMethods as DashboardFiltersType['ticketsIds'],
          }));
        }
      }}
      options={tickets.data.map((ticket) => ({
        label: ticket.title,
        value: ticket.id,
      }))}
    />
  );
};

export const DashboardFiltersPaymentMethods = () => {
  const t = useTranslation();
  const [dashboardFilters, setDashboardFilters] = useAtom(dashboardFiltersAtom);
  const setDashboardDraftFilters = useSetAtom(dashboardDraftFiltersAtom);
  const isDesktop = window.matchMedia('(min-width: 640px)').matches;

  return (
    <MultiSelect
      optionsLimit={3}
      label={t('payment-methods')}
      placeholder={t('payment-methods')}
      defaultValue={dashboardFilters.paymentMethods}
      onValueChange={(paymentMethods) => {
        if (isDesktop) {
          setDashboardFilters((prev) => ({
            ...prev,
            paymentMethods: paymentMethods as DashboardFiltersType['paymentMethods'],
          }));
        } else {
          setDashboardDraftFilters((prev) => ({
            ...prev,
            paymentMethods: paymentMethods as DashboardFiltersType['paymentMethods'],
          }));
        }
      }}
      options={[
        { label: t('CREDIT_CARD'), value: 'CREDIT_CARD' },
        { label: t('BOLETO'), value: 'BOLETO' },
        { label: t('PIX'), value: 'PIX' },
        { label: t('FREE'), value: 'FREE' },
        { label: t('GOOGLE_PAY'), value: 'GOOGLE_PAY' },
      ]}
    />
  );
};

export const DashboardFiltersDatePicker = () => {
  const t = useTranslation();
  const [dashboardFilters, setDashboardFilters] = useAtom(dashboardFiltersAtom);
  const setDashboardDraftFilters = useSetAtom(dashboardDraftFiltersAtom);
  const isDesktop = window.matchMedia('(min-width: 640px)').matches;
  return (
    <div className='flex flex-col gap-quark'>
      <span className='ts-subtitle-xxs'>{t('date')}</span>
      <DateRangePicker
        onDateChange={(date) => {
          if (isDesktop) {
            setDashboardFilters((prev) => ({
              ...prev,
              startDate: date.from,
              endDate: date.to,
            }));
          } else {
            setDashboardDraftFilters((prev) => ({
              ...prev,
              startDate: date.from,
              endDate: date.to,
            }));
          }
        }}
        date={{ from: dashboardFilters.startDate, to: dashboardFilters.endDate }}
        key={dashboardFilters.endDate}
      />
    </div>
  );
};
